import { getQuestionNodeFromLabel, getStoreName, inputText } from "@lib/_common/util/surveyUtil";
import { addStyles } from "@lib/setupRoot";
import mainStylesheet from "./App.css?inline";
import { IHomeLocation, RestructuredLocationData, homeLocationsResponseSchema } from "./types";

const LOCALSTORAGE_API_KEY_NAME = "minted-upc-picker-api-key";

const PLANOGRAM_NAME = "Inline Plano";
const SUBMITTED_UPCS = new Set();

let IS_TOGGLE_MODAL_EVENT_LISTENER_SET = false;

export async function main(toggleModalEvent: CustomEvent) {
  console.log("Minted UPC Picker started");
  addStyles(mainStylesheet);

  if (!IS_TOGGLE_MODAL_EVENT_LISTENER_SET) {
    window.addEventListener(toggleModalEvent.type, _toggleUpcPicker);
    IS_TOGGLE_MODAL_EVENT_LISTENER_SET = true;
  }

  // document.addEventListener("keydown", (event) => {
  //   if (event.key === "Escape") window.dispatchEvent(toggleModalEvent);
  // });

  const homeLocations: IHomeLocation[] = await _fetchLocations();
  const locationData = _restructurePlano(homeLocations);

  const [, maxRowLetter, numColumns] = getGridDimensions(locationData);

  addCellsToGrid(locationData, numColumns, maxRowLetter);

  document.querySelectorAll(".card-cell").forEach((elm) => {
    elm.addEventListener("click", (event) => {
      const eventTarget = event.target as HTMLLIElement;
      if (!eventTarget.classList.contains("disabled")) {
        eventTarget.classList.toggle("cell-selected");
      }
    });
  });

  document
    .getElementById("minted-submit-container")!
    .addEventListener("click", handleMintedOOSSubmit);
}

function getGridDimensions(locationData: RestructuredLocationData) {
  const baseCompareAsciiCode = 64;
  let numRows = 0;
  let maxRowLetter = null;
  let numColumns = 0;

  for (const locationName in locationData) {
    const columnNumber = parseInt(locationName.slice(1));
    if (columnNumber > numColumns) {
      numColumns = columnNumber;
    }

    const rowLetter = locationName[0];
    const asciiValueDifference = rowLetter.charCodeAt(0) - baseCompareAsciiCode;
    if (asciiValueDifference > numRows) {
      numRows = asciiValueDifference;
      maxRowLetter = rowLetter;
    }
  }

  if (maxRowLetter === null) {
    throw new Error("max row letter is null");
  }

  return [numRows, maxRowLetter, numColumns] as const;
}

function addCellsToGrid(
  locationData: RestructuredLocationData,
  numColumns: number,
  maxRowLetter: string
): void {
  const gridContainer = document.getElementById("minted-grid-container");
  if (gridContainer === null) {
    throw new Error("Grid container is null");
  }

  gridContainer.innerText = "";
  gridContainer.style.gridTemplateColumns = `repeat(${numColumns}, 1fr)`;

  for (
    let columnNumber = 1, rowLetter = "A";
    columnNumber <= numColumns && rowLetter <= maxRowLetter;
    ++columnNumber
  ) {
    const newCell = createCell(rowLetter, columnNumber, locationData);
    gridContainer.appendChild(newCell);

    if (columnNumber == numColumns) {
      rowLetter = _getNextAlphabetChar(rowLetter);
      columnNumber = 0;
    }
  }
}

function createCell(
  rowLetter: string,
  columnNumber: number,
  locationData: RestructuredLocationData
): HTMLElement {
  const locationName = `${rowLetter}${columnNumber}`;
  const newCell = _element(/*html*/ `
    <div class="card-cell text-center d-flex flex-column justify-content-center">${locationName}</div>
  `) as HTMLElement;

  if (columnNumber % 8 === 0) {
    newCell.classList.add("me-1");
  }
  if (rowLetter === "F") {
    newCell.classList.add("mb-1");
  }

  if (!(locationName in locationData)) {
    newCell.classList.add("disabled");
    return newCell;
  }

  const productUpcs = locationData[locationName].map((product) => {
    return product.upc;
  });

  const upcsStr = productUpcs.join(",");
  newCell.dataset.upcs = upcsStr;

  return newCell;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function handleMintedOOSSubmit(_event: MouseEvent): void {
  const selectedCells = document.querySelectorAll<HTMLElement>(".cell-selected");
  const oosUpcs: string[] = [];

  selectedCells.forEach((cellNode) => {
    const upcsStr = cellNode.dataset.upcs!;
    const upcList = upcsStr.split(",");
    oosUpcs.push(...upcList);
  });

  const questionText = "Scan ALL REGULAR (non-seasonal) Minted Greeting Cards out of stocks.";
  const questionNode = getQuestionNodeFromLabel(questionText);
  if (questionNode === null) {
    throw new Error(`Question node is null for question: '${questionText}'`);
  }

  oosUpcs.forEach((upc) => {
    if (SUBMITTED_UPCS.has(upc)) {
      return;
    }

    inputText(questionText, upc, { clickSubmit: true });
    // question_node.querySelector(".add-item").click();
    SUBMITTED_UPCS.add(upc);
  });

  questionNode.scrollIntoView();
  _toggleUpcPicker();
}

function _getNextAlphabetChar(mychar: string): string {
  const charCode = mychar.charCodeAt(0);
  return String.fromCharCode(charCode + 1);
}

function _toggleUpcPicker(): void {
  const modal = document.getElementById("minted-modal-container");
  if (!modal) {
    return;
  }

  modal.hasAttribute("hidden")
    ? modal.removeAttribute("hidden")
    : modal.setAttribute("hidden", "true");
}

function _restructurePlano(homeLocations: IHomeLocation[]): RestructuredLocationData {
  const locationdata = {} as RestructuredLocationData;
  homeLocations.forEach((location) => {
    locationdata[location.name] = location.products;
  });

  return locationdata;
}

async function _fetchLocations(): Promise<IHomeLocation[]> {
  const myHeaders = new Headers();
  const apiKey = localStorage.getItem(LOCALSTORAGE_API_KEY_NAME);
  if (apiKey === null) {
    throw new Error(
      `API key could not be retrieved from local storage key: ${LOCALSTORAGE_API_KEY_NAME}`
    );
  }

  const endpointUrl = new URL(
    "https://inventory-manager.mmonj.com/product_locator/api/get_planogram_locations/"
  );
  endpointUrl.searchParams.append("planogram-name", PLANOGRAM_NAME);
  endpointUrl.searchParams.append("store-name", getStoreName().toUpperCase());

  myHeaders.append("Authorization", `Token ${apiKey}`);

  const requestOptions = {
    method: "GET",
    headers: myHeaders,
    // redirect: "follow",
  };

  const resp = await fetch(endpointUrl.href, requestOptions);
  const respJson = (await resp.json()) as IHomeLocation[];
  homeLocationsResponseSchema.parse(respJson);

  return respJson;
}

function _element(html: string): ChildNode {
  const template = document.createElement("template");
  template.innerHTML = html.trim();
  return template.content.childNodes[0];
}
